// Przycisk dodawania pozycji do koszyka - lokalna wersja zgodna z Figma
// @ts-nocheck

import React, { FC, useState } from "react";
import classnames from "classnames";
import { Cart } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";

import { useNotifications } from "hooks";
import { useDispatch, useSelector, reduxActions } from "store";
import { useGetCartsAll, usePostCartAddProductToCart, usePostCart } from "api";
import { Modal } from "components/controls";
import { AddCartForm } from "components/containers";

import styles from "theme/components/containers/AddToCartButton/AddToCartButton.module.scss";

// typ danych wejściowych
interface IProps {
  isQuantityChanges?: boolean;
  quantity: number;
  unitId: number;
  productId: number;
  large?: boolean;
  disabled?: boolean;
  updateQuantity?: () => Promise<number | undefined>;
}

const AddToCartButton: FC<IProps> = ({
  quantity,
  unitId,
  productId,
  large,
  disabled,
  isQuantityChanges,
  updateQuantity,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showWarningMessage } = useNotifications();

  // domyślny koszyk
  const { currentCartId } = useSelector((state) => state.cart);

  // czy jest modal dodawania koszyka
  const [isAddCartModal, setIsAddCartModal] = useState(false);

  // pobranie lista koszyków
  const { data: cartsData, refetch: refetchCartData } = useGetCartsAll({
    enabled: false,
  });

  const { refetch: refetchCarts } = useGetCartsAll({ enabled: false });

  // dodawanie koszyka
  const { mutate: createCart } = usePostCart({
    onSuccess: async () => {
      await refetchCarts();
    },
  });

  // dodawanie pozycji do koszyka
  const { mutate: addPositionsToCartMutate, isLoading: isAddingPositions } =
    usePostCartAddProductToCart({
      onSuccess: () => {
        refetchCartData();
      },
    });

  // funkcja: jeśli brak koszyka – tworzy nowy z domyślną nazwą i dodaje produkty; w przeciwnym razie dodaje do bieżącego
  const addPositionsToCart = async (cartId?: number | null) => {
    const validatedQuantity = isQuantityChanges
      ? await updateQuantity?.()
      : quantity;

    if (!validatedQuantity) {
      showWarningMessage(
        t("Produkt nie został dodany. Ilość została zaktualizowana")
      );
      return;
    }

    const effectiveCartId = cartId ?? currentCartId;
    const productsPayload = [
      {
        product_id: productId,
        unit_id: unitId,
        quantity: validatedQuantity || quantity,
        warehouse_id: null,
      },
    ];

    if (!effectiveCartId) {
      createCart(
        { symbol: t("Mój koszyk") },
        {
          onSuccess: ({ data: { id: newCartId } }) => {
            dispatch(reduxActions.setCurrentCartId(newCartId));
            refetchCarts();
            addPositionsToCartMutate({
              cart_id: newCartId,
              products: productsPayload,
            });
          },
        }
      );
    } else {
      addPositionsToCartMutate({
        cart_id: effectiveCartId,
        products: productsPayload,
      });
    }
  };

  return (
    <>
      <button
        disabled={disabled || isAddingPositions}
        className={classnames(styles.addToCartButton, {
          [styles.large]: large,
          [styles.disabled]: disabled || isAddingPositions,
        })}
        onClick={() => addPositionsToCart()}
      >
        {large && <span className={styles.buttonText}>{t("Do koszyka")}</span>}
        <Cart className={styles.cartIcon} />
      </button>

      {isAddCartModal && (
        <Modal
          title={t("Nowy koszyk")}
          onClose={() => setIsAddCartModal(false)}
        >
          <AddCartForm
            onCancel={() => setIsAddCartModal(false)}
            onSuccess={(cartId: number) => {
              addPositionsToCart(cartId);
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default AddToCartButton;
